import * as React from 'react';
import { Container } from 'types/Watchlist';
import pluralize from 'pluralize';
import {
  centsToDollars,
  DATE_FORMATS,
  formatGatedIn,
  formatGatedOut,
  percentageFormatter,
} from 'utils/container-transforms';
import { DateTime, Duration } from 'luxon';
import { CopyTextButton } from 'components/CopyTextButton';
import { CircularProgress } from '@mui/material';
import { EmptyReturnAvailableResponse } from 'services/clients/perDiem';
import _ from 'lodash';
import { Row, RowGroup } from '../components';

interface Props {
  container: Container;
  data?: EmptyReturnAvailableResponse;
  loading?: boolean;
  showPerDiem?: boolean;
}

export const Overview: React.FC<Props> = (props) => {
  const { container, data, loading, showPerDiem } = props;

  const pickupTimeRaw = formatGatedOut(container)?.value;
  const pickupTime = pickupTimeRaw
    ? DateTime.fromISO(pickupTimeRaw).toFormat(DATE_FORMATS.SINGLE_DATE_TIME_FORMAT)
    : null;

  const returnedTimeRaw = formatGatedIn(container)?.value;
  const returnedTime = returnedTimeRaw
    ? DateTime.fromISO(returnedTimeRaw).toFormat(DATE_FORMATS.SINGLE_DATE_TIME_FORMAT)
    : null;

  const detentionPeriod = data?.dayCount || null;

  const lastDetentionDate = data?.startDate
    ? DateTime.fromISO(data.startDate)
        .minus(Duration.fromObject({ day: 1 }))
        .toFormat(DATE_FORMATS.BE_DATE_FORMAT)
    : null;

  const charges = data?.totalCost || null;

  const opportunityToDispute = data?.opportunityToFight;

  const savings = data?.estimatedSavings;

  const email = container.shipment?.shippingLine.emails?.[0]?.emailAddress;

  return (
    <RowGroup label="Overview">
      <Row label="Pickup Time">{pickupTime}</Row>
      <Row label="Returned Time">{returnedTime}</Row>
      {showPerDiem && (
        <>
          <Row
            label="Est. Per Diem LFD"
            tooltip="The estimated deadline before the container starts incurring per diem charges, based on the date it was gated out."
          >
            {loading && <CircularProgress size={12} />}
            {lastDetentionDate}
          </Row>
          <Row label="Est. Days of Per Diem">
            {loading && <CircularProgress size={12} />}
            {detentionPeriod && `${detentionPeriod} ${pluralize('day', detentionPeriod)}`}
          </Row>
          <Row
            label="Est. Per Diem Charges"
            tooltip="The estimated amount of per diem charges based on the shipping line, container type, and number of days of per diem incurred by the container."
          >
            {loading && <CircularProgress size={12} />}
            {_.isFinite(charges) ? centsToDollars(charges as number) : null}
          </Row>
          <Row label="Disputable days" tooltip="The % of per diem days that can be disputed.">
            {loading && <CircularProgress size={12} />}
            {_.isFinite(opportunityToDispute)
              ? percentageFormatter.format(opportunityToDispute as number)
              : null}
          </Row>
          <Row
            label="Est. Savings"
            tooltip="Estimated savings are calculated by multiplying the numbers of days where you can fight be the Per Diem daily amount form the SSL"
          >
            {loading && <CircularProgress size={12} />}
            {_.isFinite(savings) ? centsToDollars(savings as number) : null}
          </Row>
          <Row label="Contact" className="space-x-2">
            <span>{email}</span>
            {email && <CopyTextButton label="Copy" value={email} />}
          </Row>
        </>
      )}
    </RowGroup>
  );
};
Overview.displayName = 'Shipments.DetailDrawer.Sections.PerDiem.Overview';
